#!/usr/bin/env node
/**
 * Generate a TypeScript helper for measurements from a JSON file.
 *
 * JSON shape (example):
 * {
 *   "physical": {
 *     "None": { "value": "", "description": "Brez enote" },
 *     "Celsius": { "value": "°C", "description": "Stopinje Celzija" }
 *   },
 *   "dataSize": {
 *     "Bit": { "value": "bit", "description": "Bit" }
 *   },
 *   "counter": {
 *     "Kilo": { "value": "k", "description": "Kilo" }
 *   }
 * }
 */
import { readFile } from "node:fs/promises";
import path from "node:path";
import { pathToFileURL } from "node:url";
import { writeTextFileIfChanged } from "./file-utils.js";

type Entry = {
  value: string;
  description?: string | null;
  descriptions?: Record<string, string | null | undefined>;
  [key: string]: unknown;
};
type MeasurementsJson = {
  schemaVersion?: unknown;
  physical?: Record<string, Entry>;
  dataSize?: Record<string, Entry>;
  counter?: Record<string, Entry>;
  [category: string]: unknown;
};

const DEFAULT_INPUT = "uns-measurements.json";
const DEFAULT_OUTPUT = path.resolve(process.cwd(), "src/uns/uns-measurements.generated.ts");

async function main() {
  const { input, output, lang } = parseArgs(process.argv.slice(2));
  await generateUnsMeasurements({ input, output, lang });
}

function parseArgs(argv: string[]) {
  let input = DEFAULT_INPUT;
  let output = DEFAULT_OUTPUT;
  let lang = "sl";

  for (let i = 0; i < argv.length; i++) {
    const arg = argv[i];
    if (arg === "--input" && argv[i + 1]) {
      input = argv[++i];
      continue;
    }
    if (arg === "--output" && argv[i + 1]) {
      output = argv[++i];
      continue;
    }
    if (arg === "--lang" && argv[i + 1]) {
      lang = argv[++i];
      continue;
    }
    if (arg === "--help" || arg === "-h") {
      printHelp();
      process.exit(0);
    }
    throw new Error(`Unknown argument: ${arg}`);
  }

  return { input, output, lang };
}

function printHelp(): void {
  console.log(`Usage: tsx packages/uns-core/src/tools/generate-uns-measurements.ts [options]

Options:
  --input <file>   Path to uns-measurements.json (default: ${DEFAULT_INPUT})
  --output <file>  Path to generated TS file (default: src/uns/uns-measurements.generated.ts)
  --lang <code>    Preferred description language (default: "sl")
  --help, -h       Show this help
`);
}

async function loadJson(filePath: string): Promise<MeasurementsJson> {
  const abs = path.resolve(process.cwd(), filePath);
  const raw = await readFile(abs, "utf8");
  return JSON.parse(raw) as MeasurementsJson;
}

const resolveDesc = (entry: Entry | undefined, lang: string): string | undefined => {
  if (!entry) return undefined;
  const byLang = entry.descriptions?.[lang];
  if (byLang && byLang.length > 0) return byLang;
  if (entry.description && entry.description.length > 0) return entry.description;
  return undefined;
};

function renderSection(name: string, entries: Record<string, Entry> | undefined, lang: string): string {
  if (!entries) return `export const ${name} = {} as const;`;
  const lines = Object.entries(entries)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, entry]) => {
      const desc = resolveDesc(entry, lang);
      const doc = renderDoc(desc, "  ");
      return `${doc}  "${key}": "${entry.value}",`;
    })
    .join("\n");
  return `export const ${name} = {\n${lines}\n} as const;`;
}

function renderDoc(description: string | undefined, indent: string): string {
  if (!description) return "";
  const normalized = description
    .replaceAll("*/", "*\\/")
    .split(/\r?\n/)
    .map((line) => `${indent} * ${line}`)
    .join("\n");
  return `${indent}/**\n${normalized}\n${indent} */\n`;
}

export function renderMeasurementsTs(json: MeasurementsJson, lang: string): string {
  const physical = renderSection("GeneratedPhysicalMeasurements", json.physical, lang);
  const dataSize = renderSection("GeneratedDataSizeMeasurements", json.dataSize, lang);
  const counter = renderSection("GeneratedCounterMeasurements", json.counter, lang);

  return `/* Auto-generated by generate-uns-measurements.ts. Do not edit by hand. */\n${physical}\n\n${dataSize}\n\n${counter}\n\nexport type GeneratedPhysicalMeasurement = typeof GeneratedPhysicalMeasurements[keyof typeof GeneratedPhysicalMeasurements];\nexport type GeneratedDataSizeMeasurement = typeof GeneratedDataSizeMeasurements[keyof typeof GeneratedDataSizeMeasurements];\nexport type GeneratedCounterMeasurement = typeof GeneratedCounterMeasurements[keyof typeof GeneratedCounterMeasurements];\nexport type GeneratedMeasurementUnit = GeneratedPhysicalMeasurement | GeneratedDataSizeMeasurement | GeneratedCounterMeasurement | (string & {});\n`;
}

export async function generateUnsMeasurements(
  args: Partial<{ input: string; output: string; lang: string }>,
): Promise<void> {
  const input = args.input ?? DEFAULT_INPUT;
  const output = args.output ?? DEFAULT_OUTPUT;
  const lang = args.lang ?? "sl";

  const json = await loadJson(input);
  const content = renderMeasurementsTs(json, lang);
  const changed = await writeTextFileIfChanged(output, content);
  console.log(`Generated measurements -> ${output}${changed ? "" : " (unchanged)"}`);
}

const isDirectExecution = process.argv[1]
  ? import.meta.url === pathToFileURL(path.resolve(process.argv[1])).href
  : false;

if (isDirectExecution) {
  main().catch((err) => {
    console.error("Failed to generate measurements:", err);
    process.exitCode = 1;
  });
}
